import { useState, useEffect } from "react";
import { ReceiptUsageClient, type UsageData } from "@/services/usageService";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

export function UsagePage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [usageData, setUsageData] = useState<UsageData | null>(null);
    const [selectedMonth, setSelectedMonth] =
        useState<string>(getCurrentMonth());

    const usageClient = new ReceiptUsageClient();

    // Generate last 12 months for the dropdown
    const months = getLast12Months();

    useEffect(() => {
        async function fetchUsageData() {
            setLoading(true);
            setError(null);

            try {
                const data = await usageClient.getTenantUsage(selectedMonth);
                setUsageData(data);
            } catch (err) {
                console.error("Error fetching usage data:", err);
                setError("Failed to load usage data. Please try again later.");
            } finally {
                setLoading(false);
            }
        }

        fetchUsageData();
    }, [selectedMonth]);

    return (
        <div className="container mx-auto py-6">
            <h1 className="text-2xl font-bold mb-6">Usage Statistics</h1>

            <div className="mb-6">
                <Label htmlFor="month-select" className="mb-2 block">
                    Select Month
                </Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-[180px]" id="month-select">
                        <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                        {months.map((month) => (
                            <SelectItem key={month.value} value={month.value}>
                                {month.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Total Receipts Processed</CardTitle>
                        <CardDescription>
                            For {formatMonthDisplay(selectedMonth)}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-12 w-24" />
                        ) : (
                            <div className="text-4xl font-bold">
                                {usageData?.count || 0}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

// Helper function to get current month in YYYY-MM format
function getCurrentMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// Helper function to format month for display
function formatMonthDisplay(month: string): string {
    const [year, monthNum] = month.split("-");
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

// Helper function to generate last 12 months
function getLast12Months() {
    const months = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        const label = date.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
        });
        months.push({ value, label });
    }

    return months;
}
